import React, { useState, FC } from "react";
import { Card } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import {
  BillingPlan,
  BillingTier,
  CreateSubscriptionResponse,
} from "@/types/billing";
import BillingAPIService from "@/services/billing-api";
import { useAuth } from "@/hooks/useAuth";
import PlanSelection from "./PlanSelection";
import AccountCreation from "./AccountCreation";
import SubscriptionPayment from "./SubscriptionPayment";
import SuccessStep from "./SuccessStep";
import PublicNavBar from "@/components/layout/PublicNavBar";
import PublicFooter from "@/components/layout/PublicFooter";

export type BillingFlowStep = "plan" | "account" | "payment" | "success";

interface SubscriptionOptions {
  discountCode?: string;
  affiliateCode?: string;
}

interface BillingFlowData {
  plan?: BillingPlan;
  tier?: BillingTier;
  email?: string;
  password?: string;
  userId?: number;
  subscription?: CreateSubscriptionResponse["subscription"];
  options?: SubscriptionOptions;
  discount_percent?: number;
}

export const BillingFlow: FC = () => {
  const { toast } = useToast();
  const { login } = useAuth();
  const [flowData, setFlowData] = useState<BillingFlowData>({});
  const [processingFreeSubscription, setProcessingFreeSubscription] =
    useState(false);

  const calculateDiscountedPrice = (
    price: number,
    discount_percent?: number,
  ): number => {
    if (!discount_percent) return price;
    return price * (1 - discount_percent / 100);
  };

  // Check if this is a free subscription (100% discount)
  const isFreeSubscription = (): boolean => {
    return flowData.discount_percent === 100;
  };

  // Process a free subscription without going through Stripe payment
  const processFreeSubscription = async () => {
    if (
      !flowData.tier?.id ||
      !flowData.email ||
      !flowData.options?.discountCode
    ) {
      return;
    }

    try {
      setProcessingFreeSubscription(true);

      // Create subscription without Stripe payment
      const result = await BillingAPIService.createSubscription({
        billingTierId: flowData.tier.id,
        billingPeriod: flowData.tier.billing_period as "monthly" | "annual",
        affiliateCode: flowData.options?.affiliateCode,
        discountCode: flowData.options.discountCode,
        email: flowData.email,
        // Pass subscription type based on plan type
        subscription_type: flowData.plan?.plan_type,
        // We don't need stripeIntentId for 100% discount
      });

      if (!result.subscription) {
        throw new Error("Failed to create free subscription");
      }

      // Store the created subscription in flow data
      setFlowData((prev) => ({
        ...prev,
        subscription: result.subscription,
      }));

      // Check if this is a contractor plan
      const isContractor = flowData.plan?.plan_type === "contractor";

      // For contractor plans, redirect to login page immediately
      if (isContractor) {
        toast({
          title: "Success!",
          description:
            "Your contractor subscription has been activated. Please log in to access your account.",
        });
        return;
      }

      // For regular plans, try auto-login
      if (flowData.password) {
        try {
          await login({ email: flowData.email, password: flowData.password });
        } catch (loginError) {
          console.error(
            "Auto-login failed, user can log in manually",
            loginError,
          );
        }
      }

      // Show success toast
      toast({
        title: "Success!",
        description: "Your subscription has been activated.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to create free subscription",
      });
    } finally {
      setProcessingFreeSubscription(false);
    }
  };

  // Determine current step based on flow data
  const determineCurrentStep = (): BillingFlowStep => {
    if (flowData.subscription) {
      return "success";
    }

    if (flowData.userId && flowData.email) {
      // Skip payment step for free subscriptions
      if (isFreeSubscription()) {
        // Process the free subscription if we haven't started yet
        if (!processingFreeSubscription) {
          processFreeSubscription();
        }
        // Show success step if processing completed successfully
        if (flowData.subscription) {
          return "success";
        }
        // Stay on the current step while processing
        return "account";
      }
      return "payment";
    }

    if (flowData.plan && flowData.tier) {
      return "account";
    }

    return "plan";
  };

  const handlePlanSelected = (
    plan: BillingPlan,
    tier: BillingTier,
    options: { discountCode?: string; affiliateCode?: string },
    discount_percent?: number,
  ) => {
    setFlowData({ plan, tier, options, discount_percent });
  };

  const handleAccountCreated = async (
    userId: number,
    email: string,
    password: string,
  ) => {
    // Add a small delay to ensure the user account is properly committed to the database
    // This helps prevent race conditions where the subscription creation happens too quickly
    // after account creation
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setFlowData((prev) => ({
      ...prev,
      userId,
      email,
      password,
    }));

    // If free subscription, process it immediately after account creation
    if (isFreeSubscription()) {
      await processFreeSubscription();
    }
  };

  const renderStep = () => {
    const currentStep = determineCurrentStep();

    switch (currentStep) {
      case "plan":
        return <PlanSelection onPlanSelected={handlePlanSelected} />;

      case "account":
        if (processingFreeSubscription) {
          return (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
              <h3 className="text-xl font-medium">
                Processing your free subscription...
              </h3>
              <p className="text-muted-foreground mt-2">
                Please wait while we set up your account and activate your
                subscription.
              </p>
            </div>
          );
        }
        return (
          <AccountCreation
            onAccountCreated={handleAccountCreated}
            userType={
              flowData.plan?.plan_type === "contractor" ? "CONTRACTOR" : "FULL"
            }
          />
        );

      case "payment":
        if (
          !flowData.plan?.id ||
          !flowData.tier?.billing_period ||
          !flowData.email
        ) {
          setFlowData({});
          return null;
        }
        return (
          <SubscriptionPayment
            planId={flowData.plan.id}
            planName={flowData.plan.name}
            amount={parseFloat(
              calculateDiscountedPrice(
                parseFloat(flowData.tier.price),
                flowData.discount_percent,
              ).toFixed(2),
            )}
            billingPeriod={flowData.tier.billing_period}
            userEmail={flowData.email}
            flowData={flowData}
          />
        );

      case "success":
        if (!flowData.plan?.name || !flowData.subscription) {
          setFlowData({});
          return null;
        }
        return (
          <SuccessStep
            planName={flowData.plan.name}
            isContractor={flowData.plan.plan_type === "contractor"}
          />
        );
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <PublicNavBar />
      <div className="flex-grow container mx-auto px-4 py-8">
        <Card className="max-w-4xl mx-auto">
          <div className="p-6">{renderStep()}</div>
        </Card>
      </div>
      <PublicFooter />
    </div>
  );
};

export default BillingFlow;
